import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'
import { PlainTextarea } from '../../../../UI/PlainField'

import styles from './style.module.scss'
import { formatDate } from '../../../../shared/datetime'

/**
 * Ausgeschriebener Unterstützungsbedarf aus Sicht der Unterstützer:innen.
 *
 * Welche Bedarfe hier erscheinen, entscheidet der Server anhand der eigenen
 * Sub-Rollen und des Bundeslandes. Ein Bedarf ohne Einschränkung erreicht alle.
 */
export default function SupporterSupportRequests() {
	const { t } = useTranslation()

	const [rows, setRows] = useState(null)
	const [noteFor, setNoteFor] = useState(null)
	const [note, setNote] = useState('')
	const [pendingId, setPendingId] = useState(null)

	const load = useCallback(() => {
		http.get(TApi.SUPPORTER_SUPPORT_REQUESTS)
			.then(({ data }) => setRows(data))
			.catch(() => {
				// Meldung kommt aus shared/http
				setRows([])
			})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		load()
	}, [load])

	const respond = (request, status) => {
		setPendingId(request.id)
		/*
		 * Die Anmerkung wird nur mitgeschickt, wenn das Feld gerade offen ist.
		 * Vorher ging bei einem Wechsel von Zusage auf Absage die bestehende
		 * Anmerkung verloren, weil hier null gesendet wurde (Anforderung #22).
		 */
		http.put(TApi.SUPPORTER_SUPPORT_REQUEST_ID(request.id), {
			status,
			...(noteFor === request.id ? { note: note || null } : {})
		})
			.then(() => {
				toast.success(status === 'ACCEPTED' ? 'Zusage übermittelt.' : 'Absage übermittelt.')
				setNoteFor(null)
				setNote('')
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setPendingId(null))
	}

	if (!rows) return <Loading />

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title="Anfragen von Junior Achievement" bg="#f8faff" />

			{!rows.length ? (
				<div className={styles.empty}>
					Derzeit liegt kein Unterstützungsbedarf vor, der zu Ihrem Profil passt.
				</div>
			) : (
				<div className={styles.list}>
					{rows.map(request => {
						const answered = Boolean(request.ownResponse)
						const expired = request.isExpired

						return (
							<article
								key={request.id}
								className={clsx(styles.card, expired && styles.cardExpired)}
							>
								<header className={styles.cardHead}>
									<div>
										<h3 className={styles.title}>{request.title}</h3>
										<span className={styles.category}>{request.category}</span>
									</div>
									{answered && (
										<span
											className={clsx(
												styles.own,
												styles[`own${request.ownResponse.status}`]
											)}
										>
											{request.ownResponse.status === 'ACCEPTED'
												? 'Sie haben zugesagt'
												: 'Sie haben abgesagt'}
										</span>
									)}
								</header>

								{request.description && (
									<p className={styles.description}>{request.description}</p>
								)}

								<div className={styles.meta}>
									{request.eventDate && (
										<span>
											Termin:{' '}
											{formatDate(request.eventDate)}
										</span>
									)}
									{request.location && <span>{request.location}</span>}
									{request.respondBy && (
										<span className={expired ? styles.expired : undefined}>
											Rückmeldung bis{' '}
											{formatDate(request.respondBy)}
											{expired && ' — abgelaufen'}
										</span>
									)}
								</div>

								{!expired && (
									<>
										{noteFor === request.id && (
											<div className={styles.noteBox}>
												<PlainTextarea
													label="Anmerkung (optional)"
													rows={2}
													value={note}
													maxLength={2000}
													onChange={e => setNote(e.target.value)}
												/>
											</div>
										)}

										<footer className={styles.actions}>
											{noteFor !== request.id && (
												<button
													type="button"
													className={styles.noteLink}
													onClick={() => {
														setNoteFor(request.id)
														setNote(request.ownResponse?.note || '')
													}}
												>
													Anmerkung hinzufügen
												</button>
											)}
											<Btn
												size="small"
												theme="success"
												disabled={pendingId === request.id}
												onClick={() => respond(request, 'ACCEPTED')}
											>
												{answered ? 'Auf Zusage ändern' : 'Zusagen'}
											</Btn>
											<Btn
												size="small"
												theme="secondary"
												disabled={pendingId === request.id}
												onClick={() => respond(request, 'DECLINED')}
											>
												{answered ? 'Auf Absage ändern' : 'Absagen'}
											</Btn>
										</footer>
									</>
								)}
							</article>
						)
					})}
				</div>
			)}
		</div>
	)
}
